import { useEffect, useState, Fragment } from "react";
import { api, formatRupiah, dispDT, dispDate, currentYM, todayYMD } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, CheckCircle, XCircle, FileImage, ChevronDown, ChevronRight, Plus, Zap } from "lucide-react";
import { PageLoader } from "@/components/PageLoader";

export default function AdminKasbon() {
  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-6xl mx-auto">
      <p className="overline">Approval</p>
      <h1 className="font-display text-3xl font-bold tracking-tight">Kelola Kasbon</h1>

      <Tabs defaultValue="pengajuan" className="mt-6">
        <TabsList>
          <TabsTrigger value="pengajuan" data-testid="kasbon-section-pengajuan">Pengajuan</TabsTrigger>
          <TabsTrigger value="rekap" data-testid="kasbon-section-rekap">Rekap Kasbon</TabsTrigger>
        </TabsList>
        <TabsContent value="pengajuan" className="mt-4">
          <PengajuanSection />
        </TabsContent>
        <TabsContent value="rekap" className="mt-4">
          <RekapSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const StatusBadge = ({ status }) => {
  const map = {
    pending: ["bg-amber-100 text-amber-800 border-amber-300", "Pending"],
    approved: ["bg-green-100 text-green-800 border-green-300", "Disetujui"],
    rejected: ["bg-red-100 text-red-800 border-red-300", "Ditolak"],
  };
  const [cls, label] = map[status] || ["", status];
  return <Badge className={`${cls} border`} variant="outline">{label}</Badge>;
};

// ============ PENGAJUAN ============
const PengajuanSection = () => {
  const [tab, setTab] = useState("pending");
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [adminNote, setAdminNote] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [proofBlob, setProofBlob] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    setLoading(true);
    return api.get("/kasbon/admin", { params: { status: tab } })
      .then((r) => setList(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, [tab]);

  const decide = async (status) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("status", status);
      fd.append("admin_note", adminNote);
      if (status === "approved" && file) fd.append("proof", file);
      await api.post(`/kasbon/${selected.id}/decide`, fd);
      toast.success(`Kasbon ${status === "approved" ? "disetujui" : "ditolak"}`);
      setSelected(null);
      setAdminNote("");
      setFile(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal");
    } finally {
      setBusy(false);
    }
  };

  const openProof = async (path) => {
    setProofBlob(null);
    try {
      const r = await api.get(`/files/proof`, { params: { path }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      setProofBlob({ url, type: r.data.type });
    } catch { toast.error("Gagal memuat"); }
  };

  return (
    <>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="kasbon-tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="kasbon-tab-approved">Disetujui</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="kasbon-tab-rejected">Ditolak</TabsTrigger>
          <TabsTrigger value="all" data-testid="kasbon-tab-all">Semua</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="mt-3">
        <ManualKasbonButton onCreated={refresh} />
      </div>

      <div className="mt-4 space-y-3">
        {loading && <PageLoader label="Memuat data kasbon..." />}
        {!loading && list.length === 0 && <p className="text-sm text-zinc-500">Tidak ada data.</p>}
        {!loading && list.map((k) => (
          <Card key={k.id} className="p-4 border border-zinc-200 shadow-none" data-testid={`adm-kasbon-${k.id}`}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <p className="font-display text-lg font-semibold">{formatRupiah(k.amount)}</p>
                <p className="text-sm text-zinc-700">{k.user?.name} <span className="text-zinc-400">({k.user?.employee_id})</span></p>
                <p className="text-xs text-zinc-500 mt-0.5">Diajukan: {dispDT(k.requested_at)}</p>
                {k.decided_at && <p className="text-xs text-zinc-500">Diputuskan: {dispDT(k.decided_at)}</p>}
                <p className="text-sm text-zinc-600 mt-1">"{k.note}"</p>
                {k.admin_note && <p className="text-xs text-zinc-500 mt-1">Catatan admin: {k.admin_note}</p>}
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={k.status} />
                {k.manual && (
                  <Badge className="bg-blue-100 text-blue-800 border-blue-300 border" variant="outline">
                    <Zap className="w-3 h-3 mr-0.5" /> Manual
                  </Badge>
                )}
                {k.proof_file && (
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" onClick={() => openProof(k.proof_file)} data-testid={`adm-view-${k.id}`}>
                        <FileImage className="w-4 h-4 mr-1" /> Bukti
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader><DialogTitle>Bukti Transfer</DialogTitle></DialogHeader>
                      {proofBlob?.type?.startsWith("image/") && <img src={proofBlob.url} alt="proof" className="max-h-[70vh] w-full object-contain" />}
                      {proofBlob?.type === "application/pdf" && <iframe src={proofBlob.url} className="w-full h-[70vh]" title="proof" />}
                    </DialogContent>
                  </Dialog>
                )}
                {k.status === "pending" && (
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button size="sm" onClick={() => setSelected(k)} data-testid={`btn-decide-${k.id}`} className="bg-primary hover:bg-primary/90">
                        Proses
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>Proses Kasbon — {formatRupiah(k.amount)}</DialogTitle></DialogHeader>
                      <div className="space-y-3">
                        <div>
                          <Label>Catatan Admin</Label>
                          <Textarea value={adminNote} onChange={(e) => setAdminNote(e.target.value)} data-testid="adm-note" />
                        </div>
                        <div>
                          <Label>Bukti Transfer (jika setuju)</Label>
                          <Input type="file" accept="image/*,.pdf" onChange={(e) => setFile(e.target.files?.[0])} data-testid="adm-proof" />
                        </div>
                      </div>
                      <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => decide("rejected")} disabled={busy} data-testid="btn-reject">
                          <XCircle className="w-4 h-4 mr-1" /> Tolak
                        </Button>
                        <Button onClick={() => decide("approved")} disabled={busy} className="bg-green-600 hover:bg-green-700" data-testid="btn-approve">
                          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle className="w-4 h-4 mr-1" /> Setujui</>}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </>
  );
};

// ============ REKAP ============
const RekapSection = () => {
  const [mode, setMode] = useState("month");
  const [ref, setRef] = useState(currentYM());
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState("all");
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [proofBlob, setProofBlob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data.filter((u) => u.role === "pegawai")));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { mode, ref };
    if (userId !== "all") params.user_id = userId;
    api.get("/kasbon/rekap", { params })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [mode, ref, userId]);

  const onMode = (v) => {
    setMode(v);
    setRef(v === "month" ? currentYM() : todayYMD());
  };

  const toggle = (uid) => setExpanded((e) => ({ ...e, [uid]: !e[uid] }));

  const openProof = async (path) => {
    setProofBlob(null);
    try {
      const r = await api.get(`/files/proof`, { params: { path }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      setProofBlob({ url, type: r.data.type });
    } catch { toast.error("Gagal memuat"); }
  };

  return (
    <>
      <Card className="p-4 border border-zinc-200 shadow-none">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <p className="overline mb-2">Mode</p>
            <Tabs value={mode} onValueChange={onMode}>
              <TabsList className="w-full grid grid-cols-3">
                <TabsTrigger value="month" data-testid="kasbon-rekap-month">Bulanan</TabsTrigger>
                <TabsTrigger value="week" data-testid="kasbon-rekap-week">Mingguan</TabsTrigger>
                <TabsTrigger value="day" data-testid="kasbon-rekap-day">Harian</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <Input
            type={mode === "month" ? "month" : "date"}
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            data-testid="kasbon-rekap-ref"
          />
          <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger data-testid="kasbon-rekap-user"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Pegawai</SelectItem>
              {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {data && (
          <p className="text-xs text-zinc-500 mt-3">Periode: {dispDate(data.start)} s.d. {dispDate(data.end)}</p>
        )}
      </Card>

      {loading && !data && <PageLoader label="Memuat rekap kasbon..." />}
      {!(loading && !data) && (
      <Card className="mt-4 border border-zinc-200 shadow-none overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8"></TableHead>
              <TableHead>Pegawai</TableHead>
              <TableHead className="text-center">Pending</TableHead>
              <TableHead className="text-center">Disetujui</TableHead>
              <TableHead className="text-center">Ditolak</TableHead>
              <TableHead className="text-right">Total Disetujui</TableHead>
              <TableHead className="text-right">Total Diajukan</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data?.rows || []).filter(r => r.items.length > 0).map((row) => {
              const open = !!expanded[row.user.id];
              return (
                <Fragment key={row.user.id}>
                  <TableRow data-testid={`kasbon-rekap-row-${row.user.employee_id}`} className="cursor-pointer hover:bg-zinc-50" onClick={() => toggle(row.user.id)}>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); toggle(row.user.id); }}>
                        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <p className="font-medium">{row.user.name}</p>
                      <p className="text-xs text-zinc-500 font-mono">{row.user.employee_id}</p>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="font-semibold">{row.count_pending}</span>
                      <span className="text-xs text-zinc-500 ml-1">({formatRupiah(row.amount_pending)})</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="font-semibold text-green-700">{row.count_approved}</span>
                      <span className="text-xs text-zinc-500 ml-1">({formatRupiah(row.amount_approved)})</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="font-semibold text-red-700">{row.count_rejected}</span>
                      <span className="text-xs text-zinc-500 ml-1">({formatRupiah(row.amount_rejected)})</span>
                    </TableCell>
                    <TableCell className="text-right font-display font-bold text-green-700">{formatRupiah(row.amount_approved)}</TableCell>
                    <TableCell className="text-right">{formatRupiah(row.total_requested)}</TableCell>
                  </TableRow>
                  {open && (
                    <TableRow>
                      <TableCell colSpan={7} className="bg-zinc-50 p-0">
                        <div className="px-6 py-4">
                          <p className="overline mb-2">Detail Pengajuan</p>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Tgl Diajukan</TableHead>
                                <TableHead className="text-right">Nominal</TableHead>
                                <TableHead>Catatan</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Tgl Diputuskan</TableHead>
                                <TableHead>Catatan Admin</TableHead>
                                <TableHead>Bukti</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {row.items.map((it) => (
                                <TableRow key={it.id}>
                                  <TableCell className="text-xs">{dispDT(it.requested_at)}</TableCell>
                                  <TableCell className="text-right font-semibold">{formatRupiah(it.amount)}</TableCell>
                                  <TableCell className="text-xs text-zinc-600 max-w-xs truncate">"{it.note}"</TableCell>
                                  <TableCell><StatusBadge status={it.status} /></TableCell>
                                  <TableCell className="text-xs">{it.decided_at ? dispDT(it.decided_at) : "—"}</TableCell>
                                  <TableCell className="text-xs text-zinc-600">{it.admin_note || "—"}</TableCell>
                                  <TableCell>
                                    {it.proof_file ? (
                                      <Dialog>
                                        <DialogTrigger asChild>
                                          <Button variant="outline" size="sm" onClick={() => openProof(it.proof_file)}>
                                            <FileImage className="w-3.5 h-3.5" />
                                          </Button>
                                        </DialogTrigger>
                                        <DialogContent className="max-w-2xl">
                                          <DialogHeader><DialogTitle>Bukti Transfer</DialogTitle></DialogHeader>
                                          {proofBlob?.type?.startsWith("image/") && <img src={proofBlob.url} alt="proof" className="max-h-[70vh] w-full object-contain" />}
                                          {proofBlob?.type === "application/pdf" && <iframe src={proofBlob.url} className="w-full h-[70vh]" title="proof" />}
                                        </DialogContent>
                                      </Dialog>
                                    ) : "—"}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
            {data && data.rows.filter(r => r.items.length > 0).length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-zinc-500 py-8">Tidak ada kasbon pada periode ini</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
      )}
    </>
  );
};


// ============ MANUAL KASBON (Admin creates directly) ============
const ManualKasbonButton = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [limit, setLimit] = useState(null);
  const [elig, setElig] = useState(null);
  const [loadingLimit, setLoadingLimit] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/users").then((r) => {
      // Filter monthly only
      setUsers(r.data.filter((u) => u.role === "pegawai" && u.pay_type !== "hourly"));
    });
  }, [open]);

  useEffect(() => {
    if (!userId) { setLimit(null); setElig(null); return; }
    setLoadingLimit(true);
    Promise.all([
      api.get(`/kasbon/limit/${userId}`).catch(() => ({ data: null })),
      api.get(`/kasbon/eligibility/${userId}`).catch(() => ({ data: null })),
    ]).then(([l, e]) => {
      setLimit(l.data);
      setElig(e.data);
    }).finally(() => setLoadingLimit(false));
  }, [userId]);

  const reset = () => {
    setUserId(""); setAmount(""); setNote(""); setFile(null); setLimit(null); setElig(null);
  };

  const submit = async () => {
    if (!userId || !amount || !note.trim()) {
      toast.error("Pegawai, nominal, dan catatan wajib diisi");
      return;
    }
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) {
      toast.error("Nominal tidak valid");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("user_id", userId);
      fd.append("amount", amt);
      fd.append("note", note.trim());
      if (file) fd.append("proof", file);
      await api.post("/kasbon/admin/create", fd);
      toast.success("Kasbon manual berhasil dicatat");
      setOpen(false);
      reset();
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-blue-600 text-blue-700 hover:bg-blue-50" data-testid="btn-manual-kasbon">
          <Plus className="w-4 h-4 mr-1" /> Kasbon Manual (Admin)
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Kasbon Manual</DialogTitle>
          <p className="text-xs text-zinc-500">
            Gunakan ini untuk mencatat kasbon yang diberikan langsung oleh admin (misalnya cash di luar jam kerja).
            Langsung ter-approve, bukti transfer opsional.
          </p>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Pegawai *</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger data-testid="manual-user">
                <SelectValue placeholder="Pilih pegawai (monthly saja)" />
              </SelectTrigger>
              <SelectContent>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name} ({u.employee_id})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {userId && elig && !elig.allowed && (
            <Card className="p-3 border-2 border-amber-300 bg-amber-50 shadow-none" data-testid="manual-lock-warning">
              <div className="flex items-start gap-2">
                <span className="text-lg">⚠️</span>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm text-amber-900">Pegawai Ini Terkunci Kasbon</p>
                  <p className="text-xs text-amber-800 mt-0.5 leading-relaxed">{elig.reason}</p>
                  <p className="text-[0.65rem] text-amber-700 mt-1 italic">
                    Anda tetap bisa lanjutkan sebagai override admin — pastikan alasan ditulis pada Catatan di bawah.
                  </p>
                </div>
              </div>
            </Card>
          )}

          {userId && (
            <Card className="p-3 border border-zinc-200 shadow-none bg-zinc-50">
              {loadingLimit ? (
                <p className="text-xs text-zinc-500">Memuat limit...</p>
              ) : limit ? (
                <div className="text-xs space-y-1">
                  <div className="flex justify-between"><span className="text-zinc-500">Gaji Pokok</span><span className="font-mono">{formatRupiah(limit.salary)}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-500">Limit Bulanan ({limit.pct}%)</span><span className="font-mono">{formatRupiah(limit.limit)}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-500">Terpakai Bulan Ini</span><span className="font-mono">{formatRupiah(limit.used)}</span></div>
                  <div className="flex justify-between pt-1 border-t border-zinc-200">
                    <span className="font-semibold">Sisa Kuota</span>
                    <span className={`font-mono font-bold ${limit.remaining > 0 ? "text-green-700" : "text-red-700"}`} data-testid="manual-remaining">
                      {formatRupiah(limit.remaining)}
                    </span>
                  </div>
                </div>
              ) : null}
            </Card>
          )}

          <div>
            <Label>Nominal (Rp) *</Label>
            <Input
              type="number"
              min="1"
              step="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="200000"
              data-testid="manual-amount"
            />
          </div>

          <div>
            <Label>Catatan *</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="misal: Cash urgent malam ini karena keluarga sakit"
              data-testid="manual-note"
            />
          </div>

          <div>
            <Label>Bukti Transfer (opsional)</Label>
            <Input
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => setFile(e.target.files?.[0])}
              data-testid="manual-proof"
            />
            <p className="text-[0.65rem] text-zinc-500 mt-1">
              Kosongkan jika diberikan tunai/cash.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
          <Button
            onClick={submit}
            disabled={busy || !userId || !amount || !note.trim()}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="btn-submit-manual"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Catat Kasbon"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
